// HOW {NAME} SPEAKS TO {PARTNER} (Haley, 2026-10-12: "How he speaks to her
// should also be very important").
//
// The emotion build measures a register per person the character shares the
// most scenes with (profile.pairs[]: lines, exclaim, contractions, words per
// line, feeling families vs their own baseline, behaviors, manner, the address
// terms aimed at that person, in-bed manner with them) and the love block keeps
// per-person tender behaviors, declarations and pet names (profile.love.with[]);
// the situation tables keep per-person odds (profile.situations_with). This
// module renders all of it for ONE partner, the person actually in the room,
// so the character speaks to them the way the archive has them speak to them.
import { castPhrase } from './castPhrase.js';

const ALIASES = { evie: 'evelyn', evelyn: 'evie', seb: 'sebastian', sebby: 'sebastian', sebastian: 'seb', dil: 'dillon', dillon: 'dil' };

function norm(s) { return String(s || '').trim().toLowerCase(); }

/** The pair row for this partner, by name or known alias; null when the archive has none. */
export function pairFor(emotion, partnerName) {
  const want = norm(partnerName);
  if (!want || !emotion || !Array.isArray(emotion.pairs)) return null;
  const alt = ALIASES[want] || null;
  return emotion.pairs.find(r => norm(r.target) === want) || (alt ? emotion.pairs.find(r => norm(r.target) === alt) : null) || null;
}

function loveWithFor(emotion, target) {
  const w = emotion?.love && Array.isArray(emotion.love.with) ? emotion.love.with : [];
  const t = norm(target); const alt = ALIASES[t] || null;
  return w.find(x => norm(x.target) === t) || (alt ? w.find(x => norm(x.target) === alt) : null) || null;
}

function situationsWithFor(emotion, target) {
  const sw = emotion?.situations_with && typeof emotion.situations_with === 'object' ? emotion.situations_with : null;
  if (!sw) return null;
  const t = norm(target); const alt = ALIASES[t] || null;
  const key = Object.keys(sw).find(k => norm(k) === t) || (alt ? Object.keys(sw).find(k => norm(k) === alt) : null);
  return key ? sw[key] : null;
}

const FAMILY_WORD = { affection: 'tender', anger: 'angry', joy: 'happy', desire: 'wanting', contempt: 'scornful', calm: 'composed', shame: 'ashamed' };

function compare(pairVal, baseVal, unit = '', more = 'more', less = 'less') {
  const a = Number(pairVal), b = Number(baseVal);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return '';
  if (b === 0) return `${a}${unit}`;
  const r = a / b;
  const word = r >= 1.25 ? `${more} than usual` : r <= 0.8 ? `${less} than usual` : 'about usual';
  return `${a}${unit} with them, ${b}${unit} usually: ${word}`;
}

/**
 * The block for one partner. '' when the archive has no register for them.
 * @param {object} char      { name, ... }
 * @param {object} emotion   the emotion profile (profile JSON)
 * @param {object} contract  the voice contract (for the overall manner / in-bed baselines)
 * @param {string} partnerName  who is in the room
 * @param {object} p         pronouns { he, He, his, him, her? }
 */
export function renderPairRegister(char, emotion, contract, partnerName, p) {
  const row = pairFor(emotion, partnerName);
  if (!row) return '';
  const name = char?.name || 'you';
  const partner = row.target || partnerName;
  const lines = [];
  lines.push(`HOW ${name.toUpperCase()} SPEAKS TO ${String(partner).toUpperCase()}, measured across ${Number(row.lines || 0).toLocaleString()} of ${p.his} lines in scenes with ${partner}:`);
  const addr = Array.isArray(row.address) ? row.address.filter(x => x && x.term) : [];
  const lw = loveWithFor(emotion, partner);
  const pet = lw && Array.isArray(lw.pet_names) ? lw.pet_names.filter(x => x && x.term) : [];
  const calls = [...addr.map(x => `"${x.term}" (${x.count})`), ...pet.filter(x => !addr.some(a => norm(a.term) === norm(x.term))).map(x => `"${x.term}" (${x.count})`)];
  if (calls.length) lines.push(`• What ${p.he} calls ${partner}: ${calls.slice(0, 7).join(', ')}. These are ${partner}'s and nobody else's.`);
  const base = contract?.measured || {};
  const temp = [];
  const ex = compare(row.exclaim_pct, base.punct?.exclaim, '%');
  if (ex) temp.push(`exclaims in ${ex}`);
  const co = compare(row.contr_per_1k, base.contractions?.per_1k, '/1k words', 'looser', 'more formal');
  if (co) temp.push(`contractions ${co}`);
  if (row.words_per_line != null) temp.push(`about ${Math.round(Number(row.words_per_line))} words a line`);
  if (temp.length) lines.push(`• The register with ${partner}: ${temp.join('; ')}.`);
  const mn = row.manner, bm = base.manner;
  if (mn && bm) {
    const bits = [];
    const q = compare(mn.question_pct, bm.question_pct, '%'); if (q) bits.push(`asks in ${q}`);
    const c = compare(mn.clipped_pct, bm.clipped_pct, '%', 'shorter', 'fuller'); if (c) bits.push(`clipped lines ${c}`);
    const l = compare(mn.long_run_pct, bm.long_run_pct, '%'); if (l) bits.push(`long runs ${l}`);
    if (bits.length) lines.push(`• How ${p.he} handles a conversation with ${partner}: ${bits.join('; ')}.`);
  }
  const fam = Object.entries(row.families || {}).map(([k, v]) => [k, Number(v)]).filter(([, v]) => Number.isFinite(v));
  const more = fam.filter(([, v]) => v >= 1.5).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${FAMILY_WORD[k] || k} ×${v}`);
  const less = fam.filter(([, v]) => v <= 0.6).map(([k, v]) => `${FAMILY_WORD[k] || k} ×${v}`);
  if (more.length || less.length) lines.push(`• What ${partner} brings out in ${p.him}: ${more.length ? `more ${more.join(', ')}` : ''}${more.length && less.length ? '; ' : ''}${less.length ? `less ${less.join(', ')}` : ''} (against ${p.his} own baseline).`);
  const beh = (row.behaviors || []).map(b => `${b.behavior} (${b.count})`);
  if (beh.length) lines.push(`• What ${p.he} does around ${partner}: ${beh.join(', ')}.`);
  if (lw) {
    const lb = (lw.behaviors || []).map(b => `${b.behavior} (${b.count})`).join(', ');
    lines.push(`• In love with ${partner}: ${lb || 'mostly words'}${lw.declarations ? `; says it outright ${lw.declarations}x` : ''}.`);
  }
  const ib = row.in_bed, ob = emotion?.love?.in_bed;
  if (ib && typeof ib === 'object') {
    const vs = (field, value) => { const ph = castPhrase(value, ob?.cast?.[field]); return ph ? ` (${ph})` : ''; };
    lines.push(`• In bed with ${partner}, as played across ${ib.lines} of ${p.his} lines there: orders ${ib.commands_pct || 0}%${vs('commands_pct', ib.commands_pct)}; possessive out loud ${ib.possessive_per_100 || 0} per 100${vs('possessive_per_100', ib.possessive_per_100)}; withholds ${ib.withholds_per_100 || 0} per 100${vs('withholds_per_100', ib.withholds_per_100)}; teasing beside ${p.his} lines ${ib.teases_per_100 ?? 0} per 100; rough ${ib.rough_per_100 ?? 0} / gentle ${ib.gentle_per_100 ?? 0} per 100.`);
  }
  const sw = situationsWithFor(emotion, partner);
  if (sw && Array.isArray(sw.table) && sw.table.length) {
    const rows = sw.table.slice(0, 8).map(r => `${r.label}: ${(r.responses || []).slice(0, 2).map(x => `${x.behavior} (${x.pct}%${x.base_pct != null ? `, usually ${x.base_pct}%` : ''})`).join(', ')}`);
    lines.push(`• Most likely with ${partner} in particular (${Number(sw.exchanges || 0).toLocaleString()} exchanges): ${rows.join('; ')}.`);
  }
  lines.push(`Every line to ${partner} is in this register. ${p.He} does not speak to ${partner} the way ${p.he} speaks to the room, and ${p.he} never borrows another person's names or tone for ${partner}.`);
  return lines.join('\n');
}

/** One depth-0 line for this partner, '' when there is no register. */
export function pairCheckText(who, emotion, partnerName, dialect = 'claude') {
  const row = pairFor(emotion, partnerName);
  if (!row) return '';
  const partner = row.target || partnerName;
  const calls = (Array.isArray(row.address) ? row.address : []).slice(0, 2).map(x => `"${x.term}"`);
  const beh = (row.behaviors || []).slice(0, 2).map(b => b.behavior);
  const bits = [calls.length ? `calls ${partner} ${calls.join(' or ')}` : '', beh.length ? beh.join(', ') : ''].filter(Boolean).join('; ');
  return dialect !== 'claude'
    ? `- Every line to ${partner} is in the register ${who} uses with ${partner}${bits ? ` (${bits})` : ''}, never the room's.`
    : `every line to ${partner} is in the register you use with ${partner}${bits ? ` (${bits})` : ''}`;
}
